/**
 * CompanyBankAccountsViewElement — blok „Konta bankowe" jako przenośny klocek kreatora
 * layoutu (konfigurowalne zakładki, kartoteka Firmy). Mirror `ContractorBankAccountsViewElement`
 * (ten sam stały zestaw kolumn CORE konta + ta sama formatka `FormatkaReorderList`), różnica
 * to `type`/`allowedModules`/`elementType` (firmowy read-side `CompanyBankAccountsTab`).
 *
 * Etykiety kolumn re-używają istniejących kluczy `tenant.contractors.bankAccountsBlock.*`
 * (są generyczne dla kont bankowych, nie kontrahento-specyficzne) — bez dublowania i18n.
 * `fullWidth: true`; read-side czyta `settings.hiddenColumnIds`/`columnOrder`.
 */

import { Landmark } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';
import { applyOrder, parseSlugList, reorderSlugs } from '@/lib/kartoteki/formatka-order';

import { FormatkaReorderList } from './FormatkaReorderList';

/** Stałe kolumny CORE listy kont bankowych (slug = `id` kolumny w `ConfigurableTable`). */
const BANK_ACCOUNT_COLUMNS: ReadonlyArray<{
  slug: string;
  labelKey: string;
  defaultLabel: string;
}> = [
  {
    slug: 'account_number',
    labelKey: 'tenant.contractors.bankAccountsBlock.columns.accountNumber',
    defaultLabel: 'Numer rachunku',
  },
  {
    slug: 'bank_name',
    labelKey: 'tenant.contractors.bankAccountsBlock.columns.bankName',
    defaultLabel: 'Bank',
  },
  {
    slug: 'swift_bic',
    labelKey: 'tenant.contractors.bankAccountsBlock.columns.swiftBic',
    defaultLabel: 'SWIFT / BIC',
  },
  {
    slug: 'currency_code',
    labelKey: 'tenant.contractors.bankAccountsBlock.columns.currency',
    defaultLabel: 'Waluta',
  },
  {
    slug: 'is_primary',
    labelKey: 'tenant.contractors.bankAccountsBlock.columns.isPrimary',
    defaultLabel: 'Główne',
  },
  {
    slug: 'is_active',
    labelKey: 'tenant.contractors.bankAccountsBlock.columns.isActive',
    defaultLabel: 'Aktywne',
  },
];

const COLUMN_BY_SLUG = new Map(BANK_ACCOUNT_COLUMNS.map((c) => [c.slug, c]));

/** Bezpieczny odczyt `settings.hiddenColumnIds` → lista slugów ukrytych kolumn. */
export function parseHiddenCompanyBankAccountColumns(
  settings: Record<string, unknown> | undefined,
): string[] {
  const raw = settings?.hiddenColumnIds;
  if (!Array.isArray(raw)) return [];
  return raw.filter((s): s is string => typeof s === 'string');
}

export const CompanyBankAccountsViewElement: LayoutElementDefinition = {
  type: 'company_bank_accounts_view',
  category: 'tresc',
  isContainer: false,
  fullWidth: true,
  label: 'Konta bankowe',
  icon: 'landmark',
  allowedModules: ['companies'],
  defaultSettings: () => ({}),
  render: ({ element, mode, onSettingsChange, systemHideOnly }) => {
    const hidden = new Set(parseHiddenCompanyBankAccountColumns(element.settings));
    const orderedSlugs = applyOrder(
      BANK_ACCOUNT_COLUMNS.map((c) => c.slug),
      parseSlugList(element.settings, 'columnOrder'),
    );
    const canHide = mode === 'builder' && !!onSettingsChange;
    // Systemowy layout: tylko ukrywanie (bez reorderu), oko przebija inertny canvas.
    const canReorder = canHide && !systemHideOnly;

    const items = orderedSlugs.map((slug) => {
      const col = COLUMN_BY_SLUG.get(slug);
      return {
        slug,
        label: col ? i18n.t(col.labelKey, { defaultValue: col.defaultLabel }) : slug,
        hint: i18n.t('tenant.contractors.bankAccountsBlock.columnKind', {
          defaultValue: 'kolumna',
        }),
      };
    });

    return (
      <FormatkaReorderList
        elementId={element.id}
        items={items}
        hiddenSlugs={hidden}
        canHide={canHide}
        canReorder={canReorder}
        pierceLock={!!systemHideOnly}
        onReorder={(active, over) =>
          onSettingsChange?.({ columnOrder: reorderSlugs(orderedSlugs, active, over) })
        }
        onToggleHidden={(slug) => {
          const next = new Set(hidden);
          if (next.has(slug)) next.delete(slug);
          else next.add(slug);
          onSettingsChange?.({ hiddenColumnIds: Array.from(next) });
        }}
        header={
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <Landmark className="h-4 w-4 text-text-muted" aria-hidden={true} />
            {i18n.t('tenant.contractors.bankAccountsBlock.paletteLabel', {
              defaultValue: 'Konta bankowe',
            })}
            <span className="text-text-muted">
              {' · '}
              {i18n.t('tenant.contractors.bankAccountsBlock.columnsHint', {
                defaultValue: 'kolumny listy',
              })}
            </span>
          </p>
        }
        hiddenLabel={i18n.t('tenant.contractors.bankAccountsBlock.fieldHidden', {
          defaultValue: 'ukryte',
        })}
        labels={{
          hide: i18n.t('tenant.contractors.bankAccountsBlock.hideField', {
            defaultValue: 'Ukryj kolumnę',
          }),
          show: i18n.t('tenant.contractors.bankAccountsBlock.showField', {
            defaultValue: 'Pokaż kolumnę',
          }),
          drag: i18n.t('tenant.formatkaReorder.dragHint', {
            defaultValue: 'Przeciągnij, aby zmienić kolejność',
          }),
        }}
        testId={`company-bank-accounts-view-element-${element.id}`}
        rowTestIdPrefix="company-bank-accounts-column"
        elementType="company_bank_accounts_view"
      />
    );
  },
};
